import { jsxs, jsx } from 'react/jsx-runtime';
import { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import { useWindowScroll } from 'react-use';
import { CompareTable } from './CompareTable/CompareTable.mjs';
import { SELECTED_PRODUCTS_LIMIT } from './constants.mjs';
import { Container, InterSectionObserverObject } from './ModelCompareV4.styles.mjs';
import { ProductList } from './ProductList/ProductList.mjs';
"use strict";
const MODEL_COMPARE_V4_ANCHOR = "compare";
const INTERSECTION_OBSERVER_THRESHOLD = 0.35;
const ModelCompareV4 = ({
  headerStickOffset,
  overlayTopOffset,
  showBuyButton,
  onLinkClick,
  linkToStoreTitleClick,
  additionalProductData,
  section,
  defaultSelectedProductsCount = 2,
  selectedProducts: externalSelectedProducts,
  setSelectedProducts: setExternalSelectedProducts
}) => {
  const hasProductList = useMemo(() => section.products.length > 5 && !section.isStaticTable, [section.products, section.isStaticTable]);
  const intersectionObserverObjectRef = useRef(null);
  const {
    y: scrollPositionY
  } = useWindowScroll();
  const userIsUnderneathIntersectionObserver = useMemo(() => {
    if (!intersectionObserverObjectRef.current) return false;
    return intersectionObserverObjectRef.current.getBoundingClientRect().bottom < scrollPositionY;
  }, [scrollPositionY]);
  const defaultSelectedProducts = useMemo(() => section.products.map(product => ({
    productId: product.id
  })).slice(0, hasProductList ? defaultSelectedProductsCount : section.products.length), [defaultSelectedProductsCount, hasProductList, section.products]);
  const defaultSelectedProductsRef = useRef(defaultSelectedProducts);
  const selectedProductsLimit = useMemo(() => {
    return section.products.length < SELECTED_PRODUCTS_LIMIT ? section.products.length : SELECTED_PRODUCTS_LIMIT;
  }, [section.products.length]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [hasBeenObserved, setHasBeenObserved] = useState(false);
  const showProductGroups = section.showProductGroups !== false;
  useEffect(() => {
    defaultSelectedProductsRef.current = defaultSelectedProducts;
  }, [defaultSelectedProducts]);
  useEffect(() => {
    setSelectedProducts(externalSelectedProducts || defaultSelectedProductsRef.current);
  }, [section, externalSelectedProducts]);
  const observerCallback = useCallback(entries => {
    entries.forEach(entry => entry.isIntersecting && !hasBeenObserved && setHasBeenObserved(true));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [setHasBeenObserved]);
  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      threshold: hasProductList ? INTERSECTION_OBSERVER_THRESHOLD : 0
    });
    const intersectionObserverObjectRefCurrent = intersectionObserverObjectRef.current;
    if (intersectionObserverObjectRefCurrent) observer.observe(intersectionObserverObjectRefCurrent);
    return () => {
      if (intersectionObserverObjectRefCurrent) {
        observer.unobserve(intersectionObserverObjectRefCurrent);
        observer.disconnect();
      }
    };
  }, [observerCallback, hasProductList]);
  return /* @__PURE__ */jsxs(Container, {
    children: [hasProductList && /* @__PURE__ */jsx(ProductList, {
      isOverviewPage: Boolean(section.isOverviewPage),
      initialActiveGroup: section.products[0]?.productListGroup?.id,
      products: section.products,
      selectedProducts,
      setSelectedProducts,
      setExternalState: setExternalSelectedProducts,
      hasBeenObserved: hasBeenObserved || userIsUnderneathIntersectionObserver,
      selectedProductsLimit,
      showProductGroups
    }), /* @__PURE__ */jsx(CompareTable, {
      headerStickOffset,
      overlayTopOffset,
      showBuyButton,
      onLinkClick,
      linkToStoreTitleClick,
      additionalProductData,
      selectedProducts,
      setSelectedProducts,
      setExternalSelectedProducts,
      section,
      products: section.products,
      hasBeenObserved: hasBeenObserved || userIsUnderneathIntersectionObserver,
      selectedProductsLimit,
      isOverviewPage: Boolean(section.isOverviewPage),
      isStaticTable: Boolean(section.isStaticTable)
    }), /* @__PURE__ */jsx(InterSectionObserverObject, {
      ref: intersectionObserverObjectRef
    })]
  });
};
export { MODEL_COMPARE_V4_ANCHOR, ModelCompareV4 };
//# sourceMappingURL=ModelCompareV4.mjs.map
